/**
 * GameBoard.jsx
 * Async sudden death — each player answers SD questions in their own session.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import QuestionCard from './QuestionCard';
import GameTopBar from './GameTopBar';
import ScoreBoard from './ScoreBoard';
import RoundHeader from './RoundHeader';
import ResultsScreen from './ResultsScreen';
import {
  submitAnswer,
  getCurrentQuestion,
  getCurrentRound,
  getQuestionInRound,
  getSuddenDeathQuestions,
  TOTAL_QUESTIONS,
  QUESTIONS_PER_ROUND,
  SD_QUESTIONS_PER_ROUND,
} from '../utils/gameEngine';
import { saveDailyResult, saveDailyProgress, clearDailyProgress, saveNBADailyResult, saveNBADailyProgress, clearNBADailyProgress, saveMoshDailyResult, saveMoshDailyProgress, clearMoshDailyProgress, getDailyExits, bumpDailyExits } from '../utils/dailyChallenge';
import { updatePlayerStats } from '../utils/updateStats';

const GameBoard = ({ initialGameState, onGameEnd, onGoHome, onRematch, currentUser, onShowAuth, onPlayDaily, claimedStreaks = {}, onDailyComplete }) => {
  const statsSavedRef = useRef(false);

  const isDailyChallenge = Boolean(initialGameState.isDailyChallenge);
  const gameTotalQuestions = isDailyChallenge
    ? initialGameState.totalQuestions
    : TOTAL_QUESTIONS;

  const [gameState, setGameState] = useState(initialGameState);
  const [roundBreak, setRoundBreak] = useState(
    (initialGameState.currentQuestion > 0 || initialGameState.skipOpeningBreak)
      ? { show: false, round: 1 }
      : { show: true, round: 1 }
  );
  const [gameComplete, setGameComplete] = useState(false);
  const [dailyResult, setDailyResult] = useState(null);
  const [liveMatch, setLiveMatch] = useState(null);
  const [playerUsernames, setPlayerUsernames] = useState({ p1: 'PLAYER 1', p2: 'PLAYER 2' });

  const [sdPhase, setSdPhase] = useState(null);
  const [sdQuestions, setSdQuestions] = useState([]);
  const [sdQuestionIndex, setSdQuestionIndex] = useState(0);
  const [sdScore, setSdScore] = useState(0);
  const [sdRound, setSdRound] = useState(0);
  const [sdAnswers, setSdAnswers] = useState([]);

  const lastRoundRef = useRef(1);
  const sdPhaseRef = useRef(null);
  const sdRoundRef = useRef(0);
  const isResolvingRef = useRef(false);
  const pendingSdRound = useRef(null);
  useEffect(() => { sdPhaseRef.current = sdPhase; }, [sdPhase]);
  useEffect(() => { sdRoundRef.current = sdRound; }, [sdRound]);

  // ── Progressive save ─────────────────────────────────────────────
  useEffect(() => {
    if (!gameState.matchId || !currentUser || isDailyChallenge) return;
    if (gameState.phase === 'finished') return;
    if (gameState.answers.length === 0) return;
    const isPlayer1 = gameState.playerNumber === 1;
    const answersField = isPlayer1 ? 'player1_answers' : 'player2_answers';
    supabase.from('matches')
      .update({ [answersField]: gameState.answers })
      .eq('id', gameState.matchId)
      .then(({ error }) => { if (error) console.error('Progressive save failed:', error); })
      .catch(err => console.error('Progressive save error:', err));
  }, [gameState.answers]);

  // ── Clear H2H exit counter when match finishes naturally ─────────
  useEffect(() => {
    if (!gameComplete || isDailyChallenge) return;
    const matchId = initialGameState.matchId;
    if (!matchId) return;
    try {
      const raw = localStorage.getItem('kyg_h2h_exits');
      const exits = raw ? JSON.parse(raw) : {};
      delete exits[matchId];
      localStorage.setItem('kyg_h2h_exits', JSON.stringify(exits));
    } catch {}
  }, [gameComplete]);

  // ── Confirm before leaving active game ───────────────────────────
  const handleGoHomeConfirm = useCallback(async () => {
    if (isDailyChallenge) {
      try {
        // Per-sport, per-day counter. Previously every daily read and wrote
        // data.exits on kyg_daily, so an NBA exit spent the NFL free exit and
        // the count never reset across days.
        const exitSport = initialGameState.isMoshDaily ? 'mosh'
          : initialGameState.isNBADaily ? 'nba'
          : 'nfl';
        const exits = getDailyExits(exitSport);

        if (exits >= 1) {
          const alreadyPassing = gameState.score >= 3;
          const confirmed = window.confirm(
            alreadyPassing
              ? `You've got ${gameState.score} correct — that's a passing score. Exiting now will lock in today's challenge as complete with this score. Continue?`
              : `You've already used your one free exit. You don't have a passing score yet (need 3+). Leaving now will clear your progress and you'll have to start over. Continue?`
          );
          if (!confirmed) return;
          if (alreadyPassing) {
            if (initialGameState.isMoshDaily) {
              clearMoshDailyProgress();
              saveMoshDailyResult(gameState.score, currentUser, false, gameState.answers);
            } else if (initialGameState.isNBADaily) {
              clearNBADailyProgress();
              saveNBADailyResult(gameState.score, currentUser, false, gameState.answers);
            } else {
              clearDailyProgress();
              saveDailyResult(gameState.score, currentUser, false, gameState.answers);
            }
          } else {
            if (initialGameState.isMoshDaily) {
              clearMoshDailyProgress();
            } else if (initialGameState.isNBADaily) {
              clearNBADailyProgress();
            } else {
              clearDailyProgress();
            }
          }
          onGoHome();
          return;
        }

        const confirmed = window.confirm(
          `Heads up — you get one free exit. Come back and you'll resume right where you left off. This is it!`
        );
        if (!confirmed) return;
        bumpDailyExits(exitSport);
      } catch {}

      if (initialGameState.isMoshDaily) {
        saveMoshDailyProgress(gameState.answers, gameState.currentQuestion, gameState.score);
      } else if (initialGameState.isNBADaily) {
        saveNBADailyProgress(gameState.answers, gameState.currentQuestion, gameState.score);
      } else {
        saveDailyProgress(gameState.answers, gameState.currentQuestion, gameState.score);
      }
      onGoHome();
      return;
    }
    const matchId = initialGameState.matchId;
    const isRegulation = gameState.phase === 'playing' && !roundBreak.show && sdPhase === null;

    if (isRegulation && matchId) {
      try {
        const raw = localStorage.getItem('kyg_h2h_exits');
        const allExits = raw ? JSON.parse(raw) : {};
        const exits = allExits[matchId] || 0;

        if (exits >= 1) {
          const confirmed = window.confirm(
            `You've already used your free exit. Leaving now will forfeit the match — your current score of ${gameState.score}/${gameTotalQuestions} will be locked in. Continue?`
          );
          if (!confirmed) return;

          const isPlayer1 = gameState.playerNumber === 1;
          const scoreField   = isPlayer1 ? 'player1_score'   : 'player2_score';
          const answersField = isPlayer1 ? 'player1_answers' : 'player2_answers';
          const myDoneStatus = isPlayer1 ? 'player1_done'    : 'player2_done';

          const forfeitField = isPlayer1 ? 'player1_forfeit' : 'player2_forfeit';
          const { data: match, error } = await supabase
            .from('matches')
            .update({ [scoreField]: gameState.score, status: myDoneStatus, [answersField]: gameState.answers, [forfeitField]: true })
            .eq('id', matchId)
            .select().single();

          if (!error && match) {
            const p1Done = match.player1_score !== null && match.player1_score !== undefined;
            const p2Done = match.player2_score !== null && match.player2_score !== undefined;
            if (p1Done && p2Done) {
              const oppAlsoForfeited = isPlayer1 ? match.player2_forfeit : match.player1_forfeit;
              let forfeitWinnerId;
              if (!oppAlsoForfeited) {
                // One forfeit: score wins. Tie goes to natural finisher (no SD for forfeiter).
                if (match.player1_score !== match.player2_score) {
                  forfeitWinnerId = match.player1_score > match.player2_score ? match.player1_id : match.player2_id;
                } else {
                  forfeitWinnerId = isPlayer1 ? match.player2_id : match.player1_id;
                }
              } else if (match.player1_score !== match.player2_score) {
                // Both forfeited, scores differ — higher score wins
                forfeitWinnerId = match.player1_score > match.player2_score ? match.player1_id : match.player2_id;
              } else {
                // Both forfeited, equal scores — no winner, both lose
                forfeitWinnerId = null;
              }
              const { data: finishedMatch } = await supabase.from('matches')
                .update({ status: 'finished', winner_id: forfeitWinnerId, finished_at: new Date().toISOString() })
                .eq('id', matchId)
                .select().single();
              if (finishedMatch && !statsSavedRef.current) {
                statsSavedRef.current = true;
                await updatePlayerStats(finishedMatch);
              }
            }
            // If only this player is done, opponent's handleAnswer finalization
            // will see both scores set and close out the match on their end.
          }

          onGoHome();
          return;
        }

        const confirmed = window.confirm(
          `Heads up — you get one free exit. Come back and you'll resume right where you left off. This is it!`
        );
        if (!confirmed) return;
        allExits[matchId] = 1;
        localStorage.setItem('kyg_h2h_exits', JSON.stringify(allExits));
      } catch (err) {
        console.error('H2H exit gate error:', err);
      }
      onGoHome();
      return;
    }

    // Non-regulation exit (SD, round break): plain confirm
    const confirmed = window.confirm('Are you sure you want to exit? You may forfeit this game.');
    if (confirmed) onGoHome();
  }, [isDailyChallenge, onGoHome, gameState.score, gameState.answers, gameState.currentQuestion, gameState.phase, gameState.playerNumber, currentUser, roundBreak, sdPhase, gameTotalQuestions]);

  // ── If joining a game already in SD pending ───────────────────────
  useEffect(() => {
    if (!initialGameState.startInSdPending) return;
    // Player rejoined after already submitting their SD answer — drop onto waiting screen
    if (initialGameState.alreadyAnsweredSd) {
      const currentSdRound = initialGameState.match?.sd_round ?? 0;
      setSdRound(currentSdRound);
      sdRoundRef.current = currentSdRound;
      setSdScore(initialGameState.mySubmittedSdScore ?? 0);
      sdPhaseRef.current = 'waiting';
      setSdPhase('waiting');
      return;
    }
    const matchStatus = initialGameState.match?.status;
    if (matchStatus === 'sd_waiting') {
      pendingSdRound.current = initialGameState.match?.sd_round ?? 0;
      setSdPhase('tied_break');
    } else {
      setGameComplete(true);
    }
  }, []);

  // ── Realtime subscription ─────────────────────────────────────────
  useEffect(() => {
    if (isDailyChallenge) return;
    const matchId = initialGameState.matchId;
    if (!matchId) return;
    let isMounted = true;

    const channel = supabase
      .channel(`match-${matchId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'matches',
        filter: `id=eq.${matchId}`,
      }, async (payload) => {
        if (!isMounted) return;
        const updated = payload.new;
        setLiveMatch(updated);

        if (updated.status === 'sd_pending' && sdPhaseRef.current === null) {
          setGameComplete(true);
          return;
        }

        if (updated.status === 'finished') {
          setGameState(prev => ({ ...prev, match: updated }));
          setGameComplete(true);
          if (!statsSavedRef.current) {
            statsSavedRef.current = true;
            await updatePlayerStats(updated);
          }
          return;
        }

        if (updated.status === 'sd_waiting' && sdPhaseRef.current === 'waiting') {
          pendingSdRound.current = updated.sd_round ?? 0;
          setSdPhase('tied_break');
          return;
        }

        if (updated.status === 'sd_pending' && sdPhaseRef.current === 'waiting') {
          const sdScores = updated.sd_scores || [];
          const roundData = sdScores.find(r => r.round === sdRoundRef.current);
          if (roundData && roundData.p1 !== undefined && roundData.p2 !== undefined) {
            if (!isResolvingRef.current) {
              isResolvingRef.current = true;
              await resolveSdRound(updated, roundData);
              isResolvingRef.current = false;
            }
          }
        }
      })
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, [initialGameState.matchId, isDailyChallenge]);

  // ── Load usernames when game completes ────────────────────────────
  useEffect(() => {
    if (!gameComplete || isDailyChallenge) return;
    const matchData = liveMatch || gameState.match;
    if (!matchData?.player1_id || !matchData?.player2_id) return;
    supabase
      .from('profiles')
      .select('id, username')
      .in('id', [matchData.player1_id, matchData.player2_id])
      .then(({ data }) => {
        if (!data) return;
        const p1 = data.find(p => p.id === matchData.player1_id);
        const p2 = data.find(p => p.id === matchData.player2_id);
        setPlayerUsernames({
          p1: p1?.username || 'PLAYER 1',
          p2: p2?.username || 'PLAYER 2',
        });
      })
      .catch(err => console.error('Load usernames error:', err));
  }, [gameComplete, isDailyChallenge, liveMatch, gameState.match]);

  // ── Resolve SD round ─────────────────────────────────────────────
  const resolveSdRound = useCallback(async (matchRow, roundData) => {
    const myPlayerNum = initialGameState.playerNumber;
    const myRoundScore = myPlayerNum === 1 ? roundData.p1 : roundData.p2;
    const oppRoundScore = myPlayerNum === 1 ? roundData.p2 : roundData.p1;

    try {
      if (myRoundScore > oppRoundScore) {
        const { data: finishedMatch } = await supabase
          .from('matches')
          .update({ status: 'finished', winner_id: currentUser?.id, finished_at: new Date().toISOString() })
          .eq('id', matchRow.id)
          .select().single();
        setLiveMatch(finishedMatch);
        setSdPhase(null);
        setGameComplete(true);
        if (!statsSavedRef.current) {
          statsSavedRef.current = true;
          await updatePlayerStats(finishedMatch);
        }
      } else if (oppRoundScore > myRoundScore) {
        const oppId = myPlayerNum === 1 ? matchRow.player2_id : matchRow.player1_id;
        const { data: finishedMatch } = await supabase
          .from('matches')
          .update({ status: 'finished', winner_id: oppId, finished_at: new Date().toISOString() })
          .eq('id', matchRow.id)
          .select().single();
        setLiveMatch(finishedMatch);
        setSdPhase(null);
        setGameComplete(true);
        if (!statsSavedRef.current) {
          statsSavedRef.current = true;
          await updatePlayerStats(finishedMatch);
        }
      } else {
        const nextRound = sdRoundRef.current + 1;
        await supabase.from('matches')
          .update({ sd_round: nextRound, status: 'sd_waiting' })
          .eq('id', matchRow.id);
        pendingSdRound.current = nextRound;
        setSdPhase('tied_break');
      }
    } catch (err) {
      console.error('resolveSdRound error:', err);
      isResolvingRef.current = false; // reset lock so realtime event can retry
    }
  }, [initialGameState.playerNumber, currentUser]);

  // ── Start SD ─────────────────────────────────────────────────────
  const startSuddenDeath = useCallback((round = 0) => {
    const sdState = { ...gameState, sdRound: round };
    const qs = getSuddenDeathQuestions(sdState);
    setSdRound(round);
    sdRoundRef.current = round;
    setSdQuestions(qs);
    setSdQuestionIndex(0);
    setSdScore(0);
    setSdPhase('playing');
  }, [gameState]);

  // ── Handle SD answer ─────────────────────────────────────────────
  const handleSdAnswer = useCallback(async (answer, isCorrect) => {
    const newScore = isCorrect ? sdScore + 1 : sdScore;
    const nextIndex = sdQuestionIndex + 1;
    setSdScore(newScore);

    const currentSdQ = sdQuestions[sdQuestionIndex];
    setSdAnswers(prev => [...prev, {
      sdRound,
      player: currentSdQ?.player,
      college: currentSdQ?.college,
      answer,
      isCorrect,
    }]);

    if (nextIndex >= SD_QUESTIONS_PER_ROUND) {
      setSdPhase('waiting');
      sdPhaseRef.current = 'waiting';
      const matchId = gameState.matchId;
      const myPlayerNum = initialGameState.playerNumber;

      try {
        const { data: matchData } = await supabase
          .from('matches')
          .select('sd_scores, sd_round')
          .eq('id', matchId)
          .single();

        const existingScores = matchData?.sd_scores || [];
        const roundEntry = existingScores.find(r => r.round === sdRound) || { round: sdRound };
        if (myPlayerNum === 1) roundEntry.p1 = newScore;
        else roundEntry.p2 = newScore;

        const updatedScores = [
          ...existingScores.filter(r => r.round !== sdRound),
          roundEntry,
        ];

        const sdAnswersField = myPlayerNum === 1 ? 'sd_player1_answers' : 'sd_player2_answers';
        const { data: existingMatch } = await supabase
          .from('matches').select('sd_player1_answers, sd_player2_answers').eq('id', matchId).single();
        const existingSdAnswers = (myPlayerNum === 1
          ? existingMatch?.sd_player1_answers
          : existingMatch?.sd_player2_answers) || [];
        const currentAnswer = {
          sdRound,
          player: currentSdQ?.player,
          college: currentSdQ?.college,
          answer,
          isCorrect,
        };
        const mergedSdAnswers = [...existingSdAnswers, ...sdAnswers.filter(a => a.sdRound === sdRound), currentAnswer];

        const { data: updatedMatch } = await supabase
          .from('matches')
          .update({
            sd_scores: updatedScores,
            status: 'sd_pending',
            sd_round: sdRound,
            [sdAnswersField]: mergedSdAnswers,
          })
          .eq('id', matchId)
          .select().single();

        const myRound = updatedScores.find(r => r.round === sdRound);
        const bothDone = myRound && myRound.p1 !== undefined && myRound.p2 !== undefined;

        if (bothDone) {
          if (!isResolvingRef.current) {
            isResolvingRef.current = true;
            await resolveSdRound(updatedMatch, myRound);
            isResolvingRef.current = false;
          }
        }
      } catch (err) {
        console.error('handleSdAnswer error:', err);
        // keep sdPhase as 'waiting' — don't strand them mid-game
      }
    } else {
      setSdQuestionIndex(nextIndex);
    }
  }, [sdScore, sdQuestionIndex, sdRound, sdQuestions, gameState.matchId, initialGameState.playerNumber, resolveSdRound]);

  // ── Regulation answer handler ─────────────────────────────────────
  const handleAnswer = useCallback((answer, isCorrect) => {
    setGameState((prev) => {
      const newState = submitAnswer(prev, answer, isCorrect);

      if (newState.phase === 'finished' && isDailyChallenge) {
        const finalScore = newState.score;
        setTimeout(() => {
          (async () => {
            if (initialGameState.isMoshDaily) {
              clearMoshDailyProgress();
              const result = await saveMoshDailyResult(finalScore, currentUser, true, newState.answers);
              setDailyResult(result);
              if (result && onDailyComplete) onDailyComplete('mosh', result.streak, result.ironmanStreak);
            } else if (initialGameState.isNBADaily) {
              clearNBADailyProgress();
              const result = await saveNBADailyResult(finalScore, currentUser, true, newState.answers);
              setDailyResult(result);
              if (result && onDailyComplete) onDailyComplete('nba', result.streak, result.ironmanStreak);
            } else {
              clearDailyProgress();
              const result = await saveDailyResult(finalScore, currentUser, true, newState.answers);
              setDailyResult(result);
              if (result && onDailyComplete) onDailyComplete('nfl', result.streak, result.ironmanStreak);
            }
            setGameComplete(true);
          })();
        }, 100);
        return newState;
      }

      if (newState.phase === 'finished' && !isDailyChallenge) {
        if (newState.matchId && currentUser) {
          const isPlayer1 = newState.playerNumber === 1;
          const scoreField = isPlayer1 ? 'player1_score' : 'player2_score';
          const answersField = isPlayer1 ? 'player1_answers' : 'player2_answers';
          const myDoneStatus = isPlayer1 ? 'player1_done' : 'player2_done';

          supabase
            .from('matches')
            .update({ [scoreField]: newState.score, status: myDoneStatus, [answersField]: newState.answers })
            .eq('id', newState.matchId)
            .select().single()
            .then(async ({ data: match, error }) => {
              if (error) { console.error('Match update failed:', error); return; }
              setGameState(prev => ({ ...prev, match }));

              const p1Done = match.player1_score !== null && match.player1_score !== undefined;
              const p2Done = match.player2_score !== null && match.player2_score !== undefined;

              if (p1Done && p2Done) {
                try {
                  const oppForfeited = isPlayer1 ? match.player2_forfeit : match.player1_forfeit;
                  if (oppForfeited) {
                    // Opponent forfeited — score wins, no SD. Tie goes to natural finisher.
                    let winnerId;
                    if (match.player1_score !== match.player2_score) {
                      winnerId = match.player1_score > match.player2_score ? match.player1_id : match.player2_id;
                    } else {
                      winnerId = isPlayer1 ? match.player1_id : match.player2_id;
                    }
                    const { data: finishedMatch } = await supabase
                      .from('matches')
                      .update({ status: 'finished', winner_id: winnerId, finished_at: new Date().toISOString() })
                      .eq('id', newState.matchId)
                      .select().single();
                    setGameState(prev => ({ ...prev, match: finishedMatch }));
                    setGameComplete(true);
                    if (!statsSavedRef.current) {
                      statsSavedRef.current = true;
                      await updatePlayerStats(finishedMatch);
                    }
                  } else if (match.player1_score === match.player2_score) {
                    await supabase.from('matches')
                      .update({ status: 'sd_pending', sd_round: 0, sd_scores: [] })
                      .eq('id', newState.matchId);
                    const sdMatch = { ...match, status: 'sd_pending' };
                    setLiveMatch(sdMatch);
                    setGameState(prev => ({ ...prev, match: sdMatch }));
                    setGameComplete(true);
                  } else {
                    const winnerId = match.player1_score > match.player2_score
                      ? match.player1_id : match.player2_id;
                    const { data: finishedMatch } = await supabase
                      .from('matches')
                      .update({ status: 'finished', winner_id: winnerId, finished_at: new Date().toISOString() })
                      .eq('id', newState.matchId)
                      .select().single();
                    setGameState(prev => ({ ...prev, match: finishedMatch }));
                    setGameComplete(true);
                    if (!statsSavedRef.current) {
                      statsSavedRef.current = true;
                      await updatePlayerStats(finishedMatch);
                    }
                  }
                } catch (err) {
                  console.error('handleAnswer finish error:', err);
                  setGameComplete(true); // still show results even if DB write failed
                }
              } else {
                setGameComplete(true);
              }
            })
            .catch(err => console.error('handleAnswer match update error:', err));
        } else {
          setTimeout(() => setGameComplete(true), 100);
        }
        return newState;
      }

      const newRound = Math.floor(newState.currentQuestion / QUESTIONS_PER_ROUND) + 1;
      if (newRound > lastRoundRef.current && newState.currentQuestion < gameTotalQuestions) {
        lastRoundRef.current = newRound;
        setTimeout(() => setRoundBreak({ show: true, round: newRound }), 100);
      }

      return newState;
    });
  }, [isDailyChallenge, gameTotalQuestions, currentUser, startSuddenDeath]);

  const handleRoundBreakBegin = useCallback(() => {
    setRoundBreak(prev => ({ ...prev, show: false }));
  }, []);

  // ── Render: STILL TIED ────────────────────────────────────────────
  if (sdPhase === 'tied_break') {
    return (
      <div className="game-board">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '16px', padding: '40px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⚡</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: 'var(--gold)' }}>STILL TIED</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', color: 'var(--text-dim)', letterSpacing: '2px', maxWidth: '260px' }}>
            NEITHER PLAYER GOT IT.<br />ONE MORE QUESTION DECIDES IT.
          </div>
          <button className="btn btn-primary" style={{ marginTop: '16px', width: '100%' }} onClick={() => startSuddenDeath(pendingSdRound.current ?? sdRound + 1)}>
            ⚡ NEXT QUESTION
          </button>
          <button className="btn btn-secondary" style={{ width: '100%' }} onClick={onGoHome}>BACK TO HOME</button>
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-dim)', fontSize: '11px', maxWidth: '260px' }}>
            You can leave — check back later for the result.
          </p>
        </div>
      </div>
    );
  }

  // ── Render: SD playing ────────────────────────────────────────────
  if (sdPhase === 'playing') {
    const currentSdQuestion = sdQuestions[sdQuestionIndex];
    if (!currentSdQuestion) {
      return (
        <div className="game-board">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '16px' }}>
            <div className="waiting-spinner" />
            <p style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-dim)', letterSpacing: '2px', fontSize: '13px' }}>LOADING...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="game-board">
        <GameTopBar onGoHome={handleGoHomeConfirm} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
          <div className="round-badge sudden-death">⚡ SUDDEN DEATH</div>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', color: 'var(--gold)', letterSpacing: '2px' }}>
            Q{sdQuestionIndex + 1} / {SD_QUESTIONS_PER_ROUND} &nbsp;|&nbsp; SCORE: {sdScore}
          </span>
        </div>
        <QuestionCard
          key={`sd-${sdRound}-${sdQuestionIndex}`}
          question={currentSdQuestion}
          mode={gameState.mode}
          phase="sudden_death"
          onAnswer={handleSdAnswer}
        />
      </div>
    );
  }

  // ── Render: SD waiting ────────────────────────────────────────────
  if (sdPhase === 'waiting') {
    return (
      <div className="game-board">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '16px', padding: '40px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⚡</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: 'var(--gold)' }}>SUDDEN DEATH</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '48px', fontWeight: 700, color: 'white', lineHeight: 1 }}>{sdScore}</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--text-dim)', letterSpacing: '2px' }}>YOUR SD SCORE / {SD_QUESTIONS_PER_ROUND}</div>
          <div className="waiting-spinner" style={{ marginTop: '24px' }} />
          <p style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-dim)', letterSpacing: '2px', fontSize: '12px' }}>WAITING FOR OPPONENT...</p>
          <button className="btn btn-secondary" style={{ marginTop: '8px', width: '100%' }} onClick={onGoHome}>BACK TO HOME</button>
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-dim)', fontSize: '11px', maxWidth: '260px' }}>
            You can leave — check back later for the result.
          </p>
        </div>
      </div>
    );
  }

  // ── Render: game complete ─────────────────────────────────────────
  if (gameComplete) {
    if (isDailyChallenge) {
      return (
        <ResultsScreen
          isDailyChallenge
          isNBADaily={initialGameState.isNBADaily}
          isMoshDaily={initialGameState.isMoshDaily}
          dailyResult={dailyResult}
          myScore={gameState.score}
          totalQuestions={gameTotalQuestions}
          myAnswers={gameState.answers}
          currentUser={currentUser}
          claimedStreak={initialGameState.isMoshDaily ? claimedStreaks?.mosh : initialGameState.isNBADaily ? claimedStreaks?.nba : claimedStreaks?.nfl}
          onNewGame={() => onGameEnd()}
          onGoHome={onGoHome}
          onShowAuth={onShowAuth}
          onPlayDaily={onPlayDaily}
        />
      );
    }

    const matchData = liveMatch || gameState.match;
    const opponentScore = gameState.playerNumber === 1
      ? matchData?.player2_score
      : matchData?.player1_score;
    const isWaiting = matchData?.status !== 'finished' && matchData?.status !== 'sd_pending';
    let outcome = 'tie';
    if (matchData?.winner_id === currentUser?.id) outcome = 'win';
    else if (matchData?.winner_id) outcome = 'loss';
    const needsSd = matchData?.status === 'sd_pending' && sdPhase === null;

    return (
      <ResultsScreen
        playerNumber={gameState.playerNumber}
        myScore={gameState.score}
        opponentScore={opponentScore ?? 0}
        totalQuestions={gameTotalQuestions}
        myAnswers={gameState.answers}
        opponentAnswers={gameState.playerNumber === 1 ? matchData?.player2_answers : matchData?.player1_answers}
        isWaitingForOpponent={isWaiting}
        gameCode={gameState.matchId}
        needsSuddenDeath={needsSd}
        sdScore={sdScore}
        sdAnswers={(() => {
  const dbAnswers = (gameState.playerNumber === 1 ? matchData?.sd_player1_answers : matchData?.sd_player2_answers) || [];
  return dbAnswers.length > 0 ? dbAnswers : sdAnswers;
})()}
        sdScores={matchData?.sd_scores || []}
        opponentSdAnswers={gameState.playerNumber === 1 ? matchData?.sd_player2_answers : matchData?.sd_player1_answers}
        mode={gameState.mode}
        outcome={outcome}
        onNewGame={() => onGameEnd()}
        onRematch={onRematch}
        onGoHome={onGoHome}
        onPlaySuddenDeath={() => startSuddenDeath(matchData?.sd_round ?? 0)}
        sport={initialGameState.sport || 'nfl'}
        p1Username={playerUsernames.p1}
        p2Username={playerUsernames.p2}
      />
    );
  }

  // ── Render: round break ───────────────────────────────────────────
  if (roundBreak.show) {
    return (
      <div className="round-break">
        <button className="btn-home-corner" onClick={handleGoHomeConfirm}>HOME</button>
        <div className="round-break-content">
          {isDailyChallenge ? (
            <>
              <div className="round-break-icon">
  {initialGameState.sport === 'nba' ? '🏀' : initialGameState.sport === 'mosh' ? '🍲' : '🏈'}
</div>
              <div className="round-break-badge">DAILY</div>
              <h2 className="round-break-title">CHALLENGE</h2>
              <p className="round-break-desc">5 questions, 7 seconds each.<br />Get 3 or more correct to keep your streak.</p>
              <div className="round-break-mode">EASY &bull; MC</div>
            </>
          ) : (
            <>
              <div className="round-break-icon">
  {initialGameState.sport === 'nba' ? '🏀' : initialGameState.sport === 'mosh' ? '🍲' : '🏈'}
</div>
              <div className="round-break-badge">ROUND</div>
              <h2 className="round-break-number">{roundBreak.round}</h2>
              <p className="round-break-desc">
                {roundBreak.round === 1 ? `${gameTotalQuestions} questions. Let's go.` : `${QUESTIONS_PER_ROUND} more questions. Stay locked in.`}
              </p>
              <div className="round-break-mode">{gameState.mode === 'easy' ? 'EASY MODE' : 'HARD MODE'}</div>
            </>
          )}
          {gameState.currentQuestion > 0 && (
            <div className="round-break-score">SCORE: {gameState.score} / {gameState.currentQuestion}</div>
          )}
          {gameState.matchId && gameState.playerNumber === 1 && (
            <button
              className="btn btn-secondary"
              style={{ width: '100%', marginBottom: '8px' }}
              onClick={async () => {
                const url = `${window.location.origin}/join/${gameState.matchId}`;
                const sport = initialGameState.sport || 'nfl';
const emoji = sport === 'nba' ? '🏀' : sport === 'mosh' ? '🍲' : '🏈';
const sportLabel = sport === 'nba' ? 'NBA' : sport === 'mosh' ? 'Mish Mosh' : 'NFL';
const text = `Do you know your guy? Accept my ${sportLabel} challenge ${emoji} ${url}`;
                try {
                  if (navigator.share) {
                    await navigator.share({ title: 'Know Your Guy', text });
                  } else {
                    await navigator.clipboard.writeText(text);
                    alert('Link copied!');
                  }
                } catch {}
              }}
            >
              ⚔️ CHALLENGE A FRIEND
            </button>
          )}
          <button className="btn btn-begin" onClick={handleRoundBreakBegin}>BEGIN</button>
          {roundBreak.round === 1 && <div className="round-break-brand">knowyourguy.com</div>}
        </div>
      </div>
    );
  }

  // ── Render: active question ───────────────────────────────────────
  const currentQuestion = getCurrentQuestion(gameState);
  if (!currentQuestion) return <div className="game-board"><p>Loading...</p></div>;

  const currentRound = getCurrentRound(gameState);
  const questionInRound = getQuestionInRound(gameState);
  // Each daily names itself in the header box. NFL carries no sport flag, so it
  // is the fallback (same three-way used everywhere else in this file).
  const dailyName = initialGameState.isMoshDaily
    ? 'Mish Mosh'
    : initialGameState.isNBADaily
    ? 'NBA'
    : 'NFL';

  return (
    <div className="game-board">
      <GameTopBar onGoHome={handleGoHomeConfirm} />
      <RoundHeader
        round={currentRound}
        questionInRound={questionInRound}
        totalQuestion={gameState.currentQuestion + 1}
        totalQuestions={gameTotalQuestions}
        phase={gameState.phase}
        isDailyChallenge={isDailyChallenge}
        answers={gameState.answers}
        score={gameState.score}
      />
      <ScoreBoard
        totalAnswered={gameState.currentQuestion}
        totalQuestions={gameTotalQuestions}
        playerNumber={gameState.playerNumber}
        isDailyChallenge={isDailyChallenge}
        title={isDailyChallenge ? dailyName : undefined}
      />
      <QuestionCard
        key={`${currentQuestion.player}-${currentQuestion.index}`}
        question={currentQuestion}
        mode={gameState.mode}
        phase={gameState.phase}
        onAnswer={handleAnswer}
      />
    </div>
  );
};

export default GameBoard;